/**
 * Password reset landing page.
 *
 * Reached via the link the admin generates from the Users page. Firebase
 * Auth signs the link with an oobCode (out-of-band code) attached to a
 * specific user's email. We:
 *   1. Verify the code is valid (also gives us back the email).
 *   2. Show a form for the new password.
 *   3. Call confirmPasswordReset() to apply it.
 *   4. Send the user to /portal to sign in with the new password.
 *
 * No backend roundtrip needed — Firebase Auth handles the verification +
 * apply directly from the client SDK.
 */

import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import { auth } from "../firebase.config";

type Status = "verifying" | "ready" | "applying" | "done" | "error";

export function Reset() {
  const nav = useNavigate();
  const [status, setStatus] = useState<Status>("verifying");
  const [email, setEmail] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  // Read mode + oobCode from the URL Firebase appended.
  const params = new URLSearchParams(window.location.search);
  const mode = params.get("mode");
  const oobCode = params.get("oobCode") || "";

  useEffect(() => {
    if (!oobCode) {
      setStatus("error");
      setMessage("This link is missing its reset code. Ask the admin for a new link.");
      return;
    }
    if (mode === "resetPassword") {
      verifyPasswordResetCode(auth, oobCode)
        .then((e) => {
          setEmail(e);
          setStatus("ready");
        })
        .catch((err) => {
          setStatus("error");
          setMessage(err?.message || "This reset link is invalid or has already been used.");
        });
    } else if (mode === "verifyEmail") {
      // Standard Firebase verifyEmail flow — apply and bounce to portal.
      applyActionCode(auth, oobCode)
        .then(() => {
          setStatus("done");
          setMessage("Email verified. You can sign in now.");
        })
        .catch((err) => {
          setStatus("error");
          setMessage(err?.message || "This verification link is invalid.");
        });
    } else {
      setStatus("error");
      setMessage(`Unknown action: ${mode}`);
    }
  }, [oobCode, mode]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password.length < 6) {
      setMessage("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setMessage("Passwords don't match.");
      return;
    }
    setStatus("applying");
    setMessage(null);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setStatus("done");
      setMessage("Password set. Redirecting to sign-in…");
      setTimeout(() => nav("/portal", { replace: true }), 1500);
    } catch (err) {
      setStatus("error");
      setMessage((err as Error).message || "Could not set password.");
    }
  }

  return (
    <div className="portal-page">
      <form onSubmit={submit} className="portal-form" style={{ width: 340 }}>
        <h1>Set a new password</h1>
        {email && <div className="muted" style={{ fontSize: 13, textAlign: "center", marginTop: -4 }}>for {email}</div>}

        {status === "verifying" && (
          <div className="muted" style={{ textAlign: "center", padding: 12 }}>Verifying link…</div>
        )}

        {(status === "ready" || status === "applying") && (
          <>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={6}
              placeholder="New password (min 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={status === "applying"}
            />
            <input
              type="password"
              autoComplete="new-password"
              required
              placeholder="Repeat password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={status === "applying"}
            />
            <button type="submit" disabled={status === "applying"}>
              {status === "applying" ? "Setting…" : "Set password"}
            </button>
          </>
        )}

        {status === "done" && (
          <div className="info" style={{ textAlign: "center" }}>{message}</div>
        )}

        {status === "error" && (
          <>
            <div className="error" style={{ textAlign: "center" }}>{message}</div>
            <button type="button" className="ghost" onClick={() => nav("/portal", { replace: true })}>
              Back to sign-in
            </button>
          </>
        )}
        {status === "ready" && message && <p role="alert" style={{ color: "var(--danger)", fontSize: 13, margin: 0 }}>{message}</p>}
      </form>
    </div>
  );
}
